<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorCode, apiErrorMessage } from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import {
  UNIT_KINDS,
  hasRooms,
  emptyUnit,
  unitToForm,
  validateUnit,
  unitPayload,
} from '../residentsForm';
import FormField from './FormField.vue';

export default {
  name: 'UnitModal',
  components: { BaseModal, FormField },
  props: {
    unit: { type: Object, default: null },
    presetCondoId: { type: String, default: '' },
  },
  emits: ['close', 'saved'],
  data() {
    const form = this.unit ? unitToForm(this.unit) : emptyUnit();
    if (!this.unit && this.presetCondoId) form.condo_id = this.presetCondoId;
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      kinds: UNIT_KINDS,
      form,
      errors: {},
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters('coLivingCondos', { condos: 'getActiveCondos' }),
    isEdit() {
      return !!this.unit;
    },
    showRooms() {
      return !this.isEdit && hasRooms(this.form.kind);
    },
  },
  methods: {
    ...mapActions('coLivingResidents', ['addUnit', 'editUnit']),
    errorText(field) {
      return this.errors[field]
        ? this.$t(`CO_LIVING.RESIDENTS.ERRORS.${this.errors[field]}`)
        : '';
    },
    showServerError(err) {
      const messages = {
        name_taken: ['name', 'UNIT_NAME_TAKEN'],
        kind_locked: ['kind', 'KIND_LOCKED'],
        rooms_not_allowed: ['rooms', 'ROOMS_NOT_ALLOWED'],
      };
      const hit = messages[apiErrorCode(err)];
      if (hit) {
        this.errors = { [hit[0]]: hit[1] };
      } else {
        useAlert(
          apiErrorMessage(this, err, 'CO_LIVING.RESIDENTS.UNIT_SAVE_FAILED')
        );
      }
    },
    async onSubmit() {
      this.errors = validateUnit(this.form, { isEdit: this.isEdit });
      if (Object.keys(this.errors).length) return;
      this.isSubmitting = true;
      const fields = unitPayload(this.form, { isEdit: this.isEdit });
      try {
        if (this.isEdit) {
          await this.editUnit({ id: this.unit.id, fields });
        } else {
          await this.addUnit(fields);
        }
        useAlert(this.$t('CO_LIVING.RESIDENTS.UNIT_SAVED'));
        this.$emit('saved');
        this.$emit('close');
      } catch (err) {
        this.showServerError(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{
        isEdit
          ? $t('CO_LIVING.RESIDENTS.EDIT_UNIT')
          : $t('CO_LIVING.RESIDENTS.ADD_UNIT')
      }}
    </template>

    <form
      class="grid grid-cols-1 gap-4"
      data-testid="co-living-unit-form"
      @submit.prevent="onSubmit"
    >
      <FormField
        v-if="!isEdit"
        :label="$t('CO_LIVING.RESIDENTS.FIELDS.CONDO')"
        for-id="co-living-unit-condo"
        :error="errorText('condo_id')"
      >
        <select
          id="co-living-unit-condo"
          v-model="form.condo_id"
          :class="[selectClass, errors.condo_id ? 'border-red-500' : '']"
          data-testid="co-living-unit-condo"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.RESIDENTS.FIELDS.CONDO_PLACEHOLDER') }}
          </option>
          <option v-for="condo in condos" :key="condo.id" :value="condo.id">
            {{ condo.name }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.RESIDENTS.FIELDS.UNIT_NUMBER')"
        for-id="co-living-unit-name"
        :error="errorText('name')"
      >
        <input
          id="co-living-unit-name"
          v-model="form.name"
          type="text"
          maxlength="60"
          :class="[fieldClass, errors.name ? 'border-red-500' : '']"
          data-testid="co-living-unit-name"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.RESIDENTS.FIELDS.KIND')"
        for-id="co-living-unit-kind"
        :error="errorText('kind')"
        :hint="$t('CO_LIVING.RESIDENTS.FIELDS.KIND_HINT')"
      >
        <select
          id="co-living-unit-kind"
          v-model="form.kind"
          :class="[selectClass, errors.kind ? 'border-red-500' : '']"
          data-testid="co-living-unit-kind"
        >
          <option v-for="kind in kinds" :key="kind" :value="kind">
            {{ $t(`CO_LIVING.RESIDENTS.KINDS.${kind.toUpperCase()}`) }}
          </option>
        </select>
      </FormField>

      <FormField
        v-if="showRooms"
        :label="$t('CO_LIVING.RESIDENTS.FIELDS.ROOMS')"
        for-id="co-living-unit-rooms"
        :error="errorText('rooms')"
        :hint="$t('CO_LIVING.RESIDENTS.FIELDS.ROOMS_HINT')"
      >
        <input
          id="co-living-unit-rooms"
          v-model="form.rooms"
          type="text"
          :class="[fieldClass, errors.rooms ? 'border-red-500' : '']"
          data-testid="co-living-unit-rooms"
        />
      </FormField>

      <label
        class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
      >
        <input
          v-model="form.active"
          type="checkbox"
          class="mb-0"
          data-testid="co-living-unit-active"
        />
        {{ $t('CO_LIVING.COMMON.ACTIVE') }}
      </label>

      <div class="flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSubmitting"
          data-testid="co-living-unit-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
